import React, { useState } from 'react';

import VisibilityIcon from '@mui/icons-material/Visibility';
import { IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';

import { type EvidenceField, getDenyReasonLabel, isOtherReason } from '~/types/claimCampaignTypes';
import type { ApprovalRequest } from '~/types/loyaltyContract';

import ApprovalRequestStatusChip from './ApprovalRequestStatusChip';
import EvidenceReviewDialog from './EvidenceReviewDialog';

interface RequestsTableProps {
  requests: ApprovalRequest[];
  campaignId: string;
  evidenceFields: EvidenceField[];
}

export default function RequestsTable({ requests, campaignId, evidenceFields }: RequestsTableProps) {
  const [reviewing, setReviewing] = useState<ApprovalRequest | null>(null);

  if (requests.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
        No requests found.
      </Typography>
    );
  }

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Customer ID</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Evidence</TableCell>
            <TableCell>Submitted</TableCell>
            <TableCell>Decided By</TableCell>
            <TableCell>Decided At</TableCell>
            <TableCell>Reason / Notes</TableCell>
            <TableCell align="right">Review</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((req) => (
            <TableRow key={req.id} hover sx={{ cursor: 'pointer' }} onClick={() => setReviewing(req)}>
              <TableCell>
                <Typography variant="body2" fontFamily="monospace">
                  {req.customerId}
                </Typography>
              </TableCell>
              <TableCell>
                <ApprovalRequestStatusChip status={req.status} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {evidenceFields.length || '—'} field(s)
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {new Date(req.createdAt).toLocaleDateString()}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {req.decidedBy ?? '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {req.decidedAt ? new Date(req.decidedAt).toLocaleDateString() : '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {isOtherReason(req.denyReason)
                    ? req.notes || 'Other'
                    : req.denyReason
                      ? getDenyReasonLabel(req.denyReason)
                      : (req.notes ?? '—')}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Review evidence">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setReviewing(req);
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {reviewing && (
        <EvidenceReviewDialog
          request={reviewing}
          campaignId={campaignId}
          evidenceFields={evidenceFields}
          onClose={() => setReviewing(null)}
        />
      )}
    </>
  );
}
